import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { formatCurrency } from '../../utils/Currency';

const BalanceTable = ({ data, currency, showValues, loading, viewMode }) => {
  const theme = useTheme();

  const getTrendIcon = (current, previous) => {
    if (!previous) return <TrendingFlatIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />;

    if (current > previous) {
      return <TrendingUpIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />;
    }
    if (current < previous) {
      return <TrendingDownIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />;
    }
    return <TrendingFlatIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />;
  };

  const getSavingsRateColor = (rate) => {
    if (rate >= 20) return theme.palette.success.main;
    if (rate >= 10) return theme.palette.warning.main;
    if (rate > 0) return theme.palette.info.main;
    return theme.palette.error.main;
  };

  return (
    <Accordion
      defaultExpanded={true}
      sx={{
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderRadius: 3,
          '&.Mui-expanded': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: theme.palette.text.secondary,
          }}
        >
          Detalhamento por Periodo
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {viewMode === 'annual' ? 'Ano' : 'Mes'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Receita
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Despesa
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Saldo
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Taxa Poupanca
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Tendencia
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell align="right"><Skeleton width={100} /></TableCell>
                    <TableCell align="right"><Skeleton width={100} /></TableCell>
                    <TableCell align="right"><Skeleton width={100} /></TableCell>
                    <TableCell align="right"><Skeleton width={60} /></TableCell>
                    <TableCell align="center"><Skeleton width={30} /></TableCell>
                  </TableRow>
                ))
              ) : data.length > 0 ? (
                data.map((item, index) => {
                  const previousItem = data[index + 1];
                  return (
                    <TableRow
                      key={item.period}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.period}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.success.main }}
                        >
                          {showValues ? formatCurrency(currency, item.cashIn) : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.error.main }}
                        >
                          {showValues ? formatCurrency(currency, item.cashOut) : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color: item.netBalance >= 0
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 600,
                          }}
                        >
                          {showValues ? formatCurrency(currency, item.netBalance) : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color: getSavingsRateColor(item.savingsRate),
                            fontWeight: 500,
                          }}
                        >
                          {showValues ? `${item.savingsRate.toFixed(1)}%` : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {showValues
                            ? getTrendIcon(item.savingsRate, previousItem?.savingsRate)
                            : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, py: 2 }}
                    >
                      Nenhum dado disponivel
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

export default BalanceTable;
